import { useRef, useEffect, useState } from 'react';
import { setDoc, doc } from 'firebase/firestore/lite';
import { db } from '../firebase';
import { DASHBOARD_CRUD, notifyDashboardCrudError, handleDashboardCrud } from '../utils/dashboardCrud';
import './dragAndDropStyles.css';

export const useDragAndDrop = (data, setData, dataRef, documentName, title, ref, onErrorCallback) => {
  const draggedRowRef = useRef(null);
  const draggedIndexRef = useRef(null);
  const isDraggingRef = useRef(false);
  const [isSaving, setIsSaving] = useState(false);

  // Keep dataRef in sync with data state
  useEffect(() => {
    dataRef.current = data;
  }, [data, dataRef]);

  const initDragAndDrop = () => {
    const sortableContainer = document.querySelector("tbody.sortable-items");
    if (!sortableContainer) {
      console.warn('initDragAndDrop: sortable container not found');
      return;
    }
    
    const draggableRows = sortableContainer.querySelectorAll("tr.draggable");
    if (draggableRows.length === 0) {
      console.warn('initDragAndDrop: no draggable rows found');
      return;
    }
    
    console.log(`initDragAndDrop: initializing ${draggableRows.length} rows`);
    
    // Remove existing listeners to avoid duplicates
    draggableRows.forEach(row => {
      row.removeEventListener('dragstart', handleDragStart);
      row.removeEventListener('dragover', handleDragOver);
      row.removeEventListener('drop', handleDrop);
      row.removeEventListener('dragend', handleDragEnd);
      row.removeEventListener('dragenter', handleDragEnter);
      row.removeEventListener('dragleave', handleDragLeave);
    });
    
    // Add drag and drop listeners to each row
    draggableRows.forEach((row, index) => {
      row.draggable = true;
      row.setAttribute('data-index', index);
      row.style.cursor = 'grab';
      row.addEventListener('dragstart', handleDragStart);
      row.addEventListener('dragover', handleDragOver);
      row.addEventListener('drop', handleDrop);
      row.addEventListener('dragend', handleDragEnd);
      row.addEventListener('dragenter', handleDragEnter);
      row.addEventListener('dragleave', handleDragLeave);
    });
    
    console.log('initDragAndDrop: initialization complete');
  };

  const handleDragStart = (e) => {
    console.log('handleDragStart called', e.target);
    
    if (isSaving) {
      console.log('handleDragStart: isSaving is true, preventing drag');
      e.preventDefault();
      return false;
    }
    
    // Don't allow drag if clicking on interactive elements
    const target = e.target;
    if (target.tagName === 'INPUT' || 
        target.tagName === 'BUTTON' || 
        target.tagName === 'A' || 
        target.tagName === 'SELECT' || 
        target.tagName === 'TEXTAREA' ||
        target.closest('button') ||
        target.closest('input') ||
        target.closest('a') ||
        target.closest('.not-draggable')) {
      console.log('handleDragStart: clicked on interactive element, preventing drag');
      e.preventDefault();
      return false;
    }
    
    console.log('handleDragStart: starting drag');
    isDraggingRef.current = true;
    draggedRowRef.current = e.currentTarget;
    draggedIndexRef.current = parseInt(e.currentTarget.getAttribute('data-index') || '0');
    e.currentTarget.classList.add('dragging');
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.currentTarget.outerHTML);
    console.log('handleDragStart: drag started from index', draggedIndexRef.current);
  };

  const handleDragOver = (e) => {
    if (e.preventDefault) {
      e.preventDefault();
    }
    e.dataTransfer.dropEffect = 'move';
    
    const draggingRow = draggedRowRef.current;
    const currentRow = e.currentTarget;
    
    if (draggingRow && currentRow !== draggingRow) {
      const allRows = Array.from(currentRow.parentNode.querySelectorAll('tr.draggable'));
      const draggingIndex = allRows.indexOf(draggingRow);
      const currentIndex = allRows.indexOf(currentRow);
      
      // Only move if the position actually changed
      if (draggingIndex !== currentIndex) {
        if (draggingIndex < currentIndex) {
          currentRow.parentNode.insertBefore(draggingRow, currentRow.nextSibling);
        } else {
          currentRow.parentNode.insertBefore(draggingRow, currentRow);
        }
        
        // Update data-index attributes after DOM manipulation
        const updatedRows = Array.from(currentRow.parentNode.querySelectorAll('tr.draggable'));
        updatedRows.forEach((row, index) => {
          row.setAttribute('data-index', index);
        });
      }
    }
    
    return false;
  };

  const handleDragEnter = (e) => {
    e.currentTarget.classList.add('drag-over');
  };

  const handleDragLeave = (e) => {
    e.currentTarget.classList.remove('drag-over');
  };

  const handleDrop = (e) => {
    if (e.stopPropagation) {
      e.stopPropagation();
    }
    
    e.currentTarget.classList.remove('drag-over');
    return false;
  };

  const handleDragEnd = async (e) => {
    e.currentTarget.classList.remove('dragging');
    isDraggingRef.current = false;
    
    // Remove drag-over class from all rows
    const sortableContainer = document.querySelector("tbody.sortable-items");
    if (sortableContainer) {
      sortableContainer.querySelectorAll('tr.drag-over').forEach(row => {
        row.classList.remove('drag-over');
      });
    }
    
    if (isSaving || !draggedRowRef.current) {
      draggedRowRef.current = null;
      draggedIndexRef.current = null;
      return;
    }
    
    if (!sortableContainer) {
      draggedRowRef.current = null;
      draggedIndexRef.current = null;
      return;
    }
    
    // Get the final order from the DOM after drag
    const allRows = Array.from(sortableContainer.querySelectorAll('tr.draggable'));
    const oldIndex = draggedIndexRef.current;
    const newIndex = allRows.indexOf(draggedRowRef.current);
    
    // If position didn't change, do nothing
    if (oldIndex === newIndex || oldIndex === -1 || newIndex === -1) {
      draggedRowRef.current = null;
      draggedIndexRef.current = null;
      return;
    }
    
    // Show loading indicator
    setIsSaving(true);
    
    // Update state and save to Firebase
    const updateStateAndSave = async () => {
      try {
        // Get the final order from DOM (in case it changed during the drag)
        const finalRows = Array.from(sortableContainer.querySelectorAll('tr.draggable'));
        const finalNewIndex = finalRows.indexOf(draggedRowRef.current);
        
        if (finalNewIndex === -1 || finalNewIndex === oldIndex) {
          setIsSaving(false);
          draggedRowRef.current = null;
          draggedIndexRef.current = null;
          return;
        }
        
        // Use current data from ref (original order before drag)
        const currentDataFromState = [...dataRef.current];
        
        // Get the moved item
        const movedItem = currentDataFromState[oldIndex];
        
        if (!movedItem) {
          setIsSaving(false);
          draggedRowRef.current = null;
          draggedIndexRef.current = null;
          return;
        }
        
        // Remove item from old position
        currentDataFromState.splice(oldIndex, 1);
        
        // Insert item at new position (use finalNewIndex from DOM)
        currentDataFromState.splice(finalNewIndex, 0, movedItem);
        
        // Update order for each item
        currentDataFromState.forEach((item, order) => {
          if (item) {
            item.order = order;
            item.orden = order;
          }
        });
        
        // Save to Firebase
        const results = await Promise.all(
          currentDataFromState.map(async (item, order) => {
            if (!item) return true;
            return handleDashboardCrud(DASHBOARD_CRUD.REORDER, title, () =>
              setDoc(doc(db, documentName, item.id), {
                ...item,
                order: order,
                orden: order,
              })
            );
          })
        );
        if (results.some((ok) => ok === false)) {
          if (onErrorCallback && typeof onErrorCallback === 'function') {
            try {
              await onErrorCallback();
            } catch (callbackError) {
              console.error('Error in onErrorCallback:', callbackError);
            }
          }
          return;
        }
      
        // Update state with new order so next drag and drop uses correct indices
        //setData(currentDataFromState);
        dataRef.current = currentDataFromState;
      
        // Wait a bit and then re-initialize drag and drop
        await new Promise(resolve => setTimeout(resolve, 300));
        initDragAndDrop();
        
        console.log(`${title} reordered successfully`);
      } catch (error) {
        notifyDashboardCrudError({
          operation: DASHBOARD_CRUD.REORDER,
          entityName: title,
          error,
        });
        // Call error callback if provided (e.g., to reload data)
        if (onErrorCallback && typeof onErrorCallback === 'function') {
          try {
            await onErrorCallback();
          } catch (callbackError) {
            console.error('Error in onErrorCallback:', callbackError);
          }
        }
      } finally {
        setIsSaving(false);
        draggedRowRef.current = null;
        draggedIndexRef.current = null;
      }
    };
    
    // Use requestAnimationFrame to ensure DOM is ready before updating state
    requestAnimationFrame(() => {
      // Use setTimeout to ensure DOM manipulation is complete
      setTimeout(updateStateAndSave, 50);
    });
  };

  // Initialize drag and drop after data is loaded and DOM is ready
  useEffect(() => {
    if (data.length === 0 || isSaving || isDraggingRef.current) return; // Don't initialize if saving, no data, or currently dragging
    
    let timeoutId;
    let retryCount = 0;
    const maxRetries = 5;
    
    const tryInit = () => {
      // Don't reinitialize if currently dragging
      if (isDraggingRef.current) {
        return;
      }
      
      const sortableContainer = document.querySelector("tbody.sortable-items");
      const draggableRows = sortableContainer?.querySelectorAll("tr.draggable");
      
      if (sortableContainer && draggableRows && draggableRows.length > 0) {
        // Check if rows already have draggable attribute
        const firstRow = draggableRows[0];
        if (firstRow && firstRow.draggable === true) {
          console.log('Drag and drop already initialized');
          return;
        }
        initDragAndDrop();
      } else if (retryCount < maxRetries) {
        retryCount++;
        timeoutId = setTimeout(tryInit, 500);
      } else {
        console.warn('Failed to initialize drag and drop after', maxRetries, 'retries');
      }
    };
    
    // Initial delay to ensure DOM is ready
    timeoutId = setTimeout(tryInit, 500);
    
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.length, isSaving]); // Re-initialize when data length changes or saving state changes

  return { initDragAndDrop, isSaving };
};

